import React from "react";
import MicPicker from "./MicPicker.jsx";

/**
 * VoicePill — the one control of a voice interview (Plan F). It says whose
 * turn it is and is where you tap: [mic ▾] state word … End.
 *
 * Pure presentation over useVoiceTurn's values. `startLabel` is what it says
 * before the first tap ("Listen then answer by voice"). `words` overrides what
 * it says in a state, for a page whose waits mean something specific.
 */
export function stateWord(state, startLabel, words) {
  if (words && words[state]) return words[state];
  switch (state) {
    case "idle":      return startLabel;
    case "joining":   return "Joining audio";
    case "reading":   return "Reading…";
    case "speak":     return "Speak now";
    case "listening": return "Listening…";
    case "thinking":  return "Thinking…";
    case "done":      return "Done — thank you";
    case "denied":    return "Microphone off — type your answers below";
    default:          return state;
  }
}

function Mic({ pulse }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         style={{ transform: `scale(${1 + Math.min(0.35, pulse * 3)})`, transition: "transform 80ms linear" }}>
      <rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><path d="M12 17v4M8 21h8"/>
    </svg>
  );
}

export default function VoicePill({ state, level = 0, startLabel = "Speak", words, onStart, onTap, onEnd,
                                    devices = [], deviceId = "", onPickDevice }) {
  if (state === "denied") {
    return <p role="status" style={v.notice}>{stateWord(state, startLabel)}</p>;
  }
  const idle = state === "idle";
  const active = state === "speak" || state === "listening";
  const tapping = state === "listening" || state === "speak" || state === "reading";
  const word = stateWord(state, startLabel, words);
  return (
    <div style={v.wrap}>
      <div role="group" aria-label="Voice" style={{ ...v.pill, ...(active ? v.pillActive : {}) }}>
        <button type="button" style={v.main} aria-label={idle ? word : `${word}. Tap to ${state === "listening" ? "send your answer" : state === "reading" ? "skip the reading" : "start speaking"}`}
                onClick={idle ? onStart : (tapping ? onTap : undefined)} disabled={!idle && !tapping}>
          <span style={{ ...v.icon, ...(state === "listening" ? v.iconOn : {}) }}>
            {idle ? <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z"/></svg>
             : state === "joining" || state === "thinking" ? <span style={v.spinner} aria-hidden="true" />
             : <Mic pulse={state === "listening" ? level : 0} />}
          </span>
          <span style={v.word}>{word}</span>
        </button>
        {!idle && <MicPicker devices={devices} deviceId={deviceId} onPick={onPickDevice} />}
        {!idle && state !== "done" && (
          <button type="button" style={v.end} onClick={onEnd}>End</button>
        )}
      </div>
    </div>
  );
}

const v = {
  wrap:      { display: "flex", justifyContent: "center", position: "sticky", top: 8, zIndex: 20, pointerEvents: "none" },
  pill:      { pointerEvents: "auto", display: "inline-flex", alignItems: "center", gap: 2, height: 56, padding: "0 8px 0 6px", borderRadius: 999, background: "var(--color-surface)", border: "1px solid var(--color-border-strong)", boxShadow: "0 4px 18px rgba(0,0,0,0.10)" },
  pillActive:{ borderColor: "var(--color-accent)" },
  main:      { font: "inherit", display: "inline-flex", alignItems: "center", gap: 10, height: 44, padding: "0 12px 0 6px", border: "none", background: "none", borderRadius: 999, cursor: "pointer", color: "var(--color-text-primary)" },
  icon:      { width: 36, height: 36, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-primary)" },
  iconOn:    { background: "var(--color-accent-subtle)", color: "var(--color-accent)" },
  word:      { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", whiteSpace: "nowrap" },
  end:       { font: "inherit", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", color: "var(--color-critical)", background: "none", border: "none", padding: "0 10px 0 12px", cursor: "pointer", height: 44 },
  spinner:   { width: 16, height: 16, borderRadius: 999, border: "2px solid var(--color-border-strong)", borderTopColor: "var(--color-accent)", animation: "cg-spin 0.9s linear infinite", display: "inline-block" },
  notice:    { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: 999, padding: "8px 16px", margin: "0 auto", width: "fit-content" },
};
